import React, { useEffect, useState } from "react"
import { Plus, Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Input,
  Badge,
  Button,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@f2e/ui/components/base"
import { ellipsis } from "@f2e/shared/utils"

import { UserAvatar, UserNameplate, FilterSelect } from "@/components"
import { FetchStatus, useSearchUserApi, userResponseToUser } from "@/hooks/api"
import { useStore } from "@/store"
import { getApiErrorPayloadMessage, normalizeApiError } from "@/lib/apiErrors"
import { User } from "@/types"

interface AddDialogProps<T> {
  title: string
  tableTitle?: string
  description: string
  emptyMessage?: string
  open?: boolean
  currentUsers: User[]
  selectableUsers?: User[]
  preNode?: React.ComponentType<PreNodeProps<T>>
  onOpenChange: (open: boolean) => void
  onConfirm: (newUsers: User[], preData?: T) => void
}

export interface PreNodeProps<T> {
  setData?: (data: T) => void
  error?: string
}

const AddDialog = <T,>({
  title,
  tableTitle = "New Users",
  description,
  emptyMessage = "No Data",
  open = false,
  currentUsers,
  selectableUsers,
  preNode,
  onOpenChange,
  onConfirm,
}: AddDialogProps<T>) => {
  const { t } = useTranslation()
  const [newUsers, setNewUsers] = useState<User[]>([])
  const [newUserEmail, setNewUserEmail] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [preErrorMessage, setPreErrorMessage] = useState("")
  const { status: searchStatus, fetchFn: searchUser } = useSearchUserApi()
  const [dropdownUsers, setDropdownUsers] = useState<User[]>([])
  const [preData, setPreData] = useState<T>()

  const reset = () => {
    setNewUsers([])
    setNewUserEmail("")
    setErrorMessage("")
    setPreData(undefined)
    setPreErrorMessage("")
  }

  useEffect(() => {
    if (!open) return
    setDropdownUsers(selectableUsers || [])
  }, [open, selectableUsers])

  const handleConfirmClick = () => {
    onConfirm(newUsers, preData)
    handleOpenChange(false)
  }

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    if (!open) {
      reset()
    }
  }

  const handleAddUser = () => {
    if (!preData) {
      setPreErrorMessage(t("common.userAddDialog.message.workspaceRequired"))
      return
    }

    if (!newUserEmail) {
      setErrorMessage(t("common.userAddDialog.message.emailRequired"))
      return
    }

    if (newUsers.find((u) => u.email === newUserEmail)) {
      setErrorMessage(t("common.userAddDialog.message.alreadyInList"))
      return
    }

    if (currentUsers.find((u) => u.email === newUserEmail)) {
      setErrorMessage(t("common.userAddDialog.message.alreadyExists"))
      return
    }

    searchUser({
      searchParams: new URLSearchParams({ email: newUserEmail }),
      callbackFn: (resp) => {
        if (resp?.error_code !== 0) {
          const normalizedError = normalizeApiError(resp)
          if (normalizedError.kind === "permission_denied") {
            setErrorMessage(
              getApiErrorPayloadMessage(
                resp,
                t,
                t("common.userAddDialog.message.userNotFound")
              )
            )
            return
          }
          const jsonString = resp?.message.replace(/'/g, '"')
          const fixedJsonString = jsonString
            ?.replace(/\(/g, "[")
            .replace(/\)/g, "]")
          const errorJson = JSON.parse(fixedJsonString || "{}")
          setErrorMessage(errorJson[0].msg || "Failed to find the user.")
          return
        }
        if (!resp || !resp.data) return

        const searchedUsers = resp.data.map((user) => userResponseToUser(user))
        if (!searchedUsers || searchedUsers.length === 0) {
          setErrorMessage(t("common.userAddDialog.message.userNotFound"))
          return
        }

        setNewUsers([...searchedUsers, ...newUsers])
        setNewUserEmail("")
        setErrorMessage("")
      },
    })
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAddUser()
    }
  }

  const handleRemoveUser = (user: User) => {
    const restUsers = newUsers.filter((u) => u.id !== user.id)
    setNewUsers(restUsers)
    setDropdownUsers([user, ...dropdownUsers])
  }

  const handleSetPreData = (data: T) => {
    setPreData(data)
    setPreErrorMessage("")
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* node before the main content */}
        {preNode &&
          React.createElement<PreNodeProps<T>>(preNode, {
            setData: handleSetPreData,
            error: preErrorMessage,
          })}

        <div className="space-y-4">
          {!selectableUsers && (
            <>
              <Label>{t("common.userAddDialog.email")}</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  value={newUserEmail}
                />
                <div className="flex flex-col justify-end">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={handleAddUser}
                    disabled={searchStatus === FetchStatus.Loading}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="text-sm text-red-500 dark:text-red-900">
                {errorMessage}
              </div>
            </>
          )}
          {selectableUsers && (
            <FilterSelect
              label={t("common.userAddDialog.selectUser")}
              align="start"
              options={dropdownUsers.map((u) => ({
                value: u.email,
                label: u.email,
                data: u,
              }))}
              onSelect={(value) => {
                const user = dropdownUsers.find((u) => u.email === value)
                if (!user) return
                setNewUsers([user, ...newUsers])
                setDropdownUsers(
                  dropdownUsers.filter((u) => u.email !== user.email)
                )
              }}
              renderOption={(option) => (
                <UserNameplate user={option.data!} size={10} />
              )}
            />
          )}
        </div>
        <div>
          <Label className="mt-4">{tableTitle}</Label>
          <UserFieldsTable
            className="min-h-[200px] max-h-[400px] overflow-y-scroll"
            users={newUsers}
            emptyMessage={emptyMessage}
            onRemove={handleRemoveUser}
          />
        </div>
        <DialogFooter>
          <Button onClick={handleConfirmClick}>{t("common.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface UserFieldsTableProps {
  className?: string
  users: User[]
  emptyMessage: string
  onRemove?: (user: User) => void
}

const UserFieldsTable = ({
  className,
  users,
  emptyMessage,
  onRemove,
}: UserFieldsTableProps) => {
  const [userMe] = useStore((state) => [state.userMe])
  const { t } = useTranslation()

  return (
    <div className={className}>
      {users.length === 0 && (
        <div className="flex flex-row h-[100px] w-full items-center justify-center text-slate-500 text-sm">
          {emptyMessage}
        </div>
      )}
      {users.length > 0 && (
        <Table className="table-fixed">
          <TableBody>
            {users.map((user, idx) => (
              <TableRow key={idx}>
                <TableCell className="w-5/6">
                  <div className="flex">
                    <div className="flex items-center">
                      <UserAvatar user={user} className="h-10 w-10" />
                      <div className="grid grid-rows-2 ml-4">
                        <div className="text-base">
                          <span>{ellipsis(user.name, 50)}</span>
                          <span>
                            {userMe?.id === user.id && (
                              <Badge variant="secondary" className="ml-2 py-0">
                                {t("common.you")}
                              </Badge>
                            )}
                          </span>
                        </div>
                        <div className="text-sm text-slate-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemove?.(user)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

export default AddDialog
